import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useRef, useState } from "react";
import QuizPurchaseDialog from "./QuizPurchaseDialog";
import { isPaidQuizLocked } from "./quizAccess";
import { useNavigate } from "react-router-dom";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  persistQuizFlowState,
} from "./quizFlowState";
import "./QuizzCard.css";
import shareIcon from "../../assets/images/sharee.png";

const colorClasses = ["green-card", "blue-card", "purple-card", "blue-card"];

const QuizzCard = ({
  items = [],
  isLoading = false,
  emptyMessage = "No quizzes available.",
  navigationState = {},
  onBeforeOpen,
  separateToday = false,
  coinBalance = 0,
}) => {
  const navigate = useNavigate();
  const opening = useRef(false);
  const [purchaseQuiz, setPurchaseQuiz] = useState(null);
  const handleOpen = async (test) => {
    if (opening.current) return;
    opening.current = true;
    try {
      if (onBeforeOpen && !(await onBeforeOpen(test))) return;
      if (isPaidQuizLocked(test)) {
        setPurchaseQuiz(test);
        return;
      }
      handleNavigate(test, true);
    } finally {
      opening.current = false;
    }
  };

  const handleNavigate = (test, attemptsOnly = false) => {
    const quizId = Number(test?.quizId || test?.quiz_id || test?.id || 0);
    const baseFlowState = buildQuizFlowState({
      ...navigationState,
      category_id: 1,
      quiz_id: quizId,
      state_name: navigationState?.selectedState?.state_name || navigationState.state_name || navigationState.stateName || '',
      source_route: Number(navigationState.series_id || navigationState.selectedSeries?.id) > 0 ? 'quizListSeries' : Number(navigationState.route_key) === 3 ? 'PYP' : 'quizScreen',
      quizData: {
        ...test,
        quizId,
        quiz_id: quizId,
        id: quizId,
        quizName: test?.title || test?.quizName || "",
        title: test?.title || test?.quizName || "",
        statesId: Number(navigationState?.selectedState?.state_id || navigationState?.state_id || 0),
        categoryId: 1,
        stateName:
          navigationState?.selectedState?.state_name ||
          navigationState?.stateName ||
          navigationState?.state_name ||
          "",
        categoryName: navigationState?.selectedQuizOption || "Tests & Quizzes",
        bannerImage: navigationState?.listBannerImage || "",
      },
      selectedQuiz: {
        ...test,
        id: quizId,
        quizId,
        quiz_id: quizId,
        title: test?.title || test?.quizName || "",
        quizName: test?.title || test?.quizName || "",
      },
      quizBackTarget: buildQuizBackTarget(navigationState?.quizListPath || QUIZ_ROUTES.list, navigationState),
      quizListBackTarget: buildQuizBackTarget(navigationState?.quizListPath || QUIZ_ROUTES.list, navigationState),
    });
    const pendingQuizInfo = test?.pendingQuizInfo || null;
    const assesmentBatchId = Number(pendingQuizInfo?.assesment_batch_id || 0);
    const isPendingQuiz =
      String(pendingQuizInfo?.quiz_action || test?.status || "")
        .trim()
        .toLowerCase() === "pending";

    if (!attemptsOnly && isPendingQuiz && assesmentBatchId > 0) {
      const directFlowState = buildQuizFlowState({
        ...baseFlowState,
        assesment_batch_id: assesmentBatchId,
        resume_on_open: true,
        selectedMode: pendingQuizInfo?.quiz_mode
          ? {
              id: String(pendingQuizInfo.quiz_mode).trim().toUpperCase(),
              dataKey: String(pendingQuizInfo.quiz_mode).trim().toUpperCase(),
              title: "Pending Quiz",
            }
          : null,
        quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, baseFlowState),
      });

      persistQuizFlowState(directFlowState);
      navigate(QUIZ_ROUTES.detail, {
        state: directFlowState,
      });
      return;
    }

    persistQuizFlowState(baseFlowState);
    navigate(QUIZ_ROUTES.start, {
      state: baseFlowState,
    });
  };

  if (separateToday && !isLoading && items.length) {
    const today = items.filter((item) => item.isToday);
    const other = items.filter((item) => !item.isToday);
    return <>
      {today.length > 0 && <section aria-label="Today's quizzes">
        <h2>Today's quizzes</h2>
        <QuizzCard items={today} navigationState={navigationState} onBeforeOpen={onBeforeOpen} coinBalance={coinBalance} />
      </section>}
      {other.length > 0 && <section aria-label="Other quizzes">
        {/* <h2>Other quizzes</h2> */}
        <QuizzCard items={other} navigationState={navigationState} onBeforeOpen={onBeforeOpen} coinBalance={coinBalance} />
      </section>}
    </>;
  }

  if (isLoading) {
    return (
      <div className="quizz-test-wrapper">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="quizz-test-card" key={`quiz-skeleton-${index}`}>
            <div className="dashboard-skeleton dashboard-skeleton--thumb" style={{ width: 56, height: 56, borderRadius: 13 }} />
            <div className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title mt-3" />
            <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
            <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
          </div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return <p className="choose-state__status">{emptyMessage}</p>;
  }

  return (
    <>
    <QuizPurchaseDialog open={Boolean(purchaseQuiz)} onClose={() => setPurchaseQuiz(null)} quiz={purchaseQuiz || {}} navigationState={navigationState} coinBalance={coinBalance} onUnlocked={test => handleNavigate(test, true)} />
    <div className="quizz-test-wrapper">
      {items.map((test, index) => (
        <div
          className="quizz-test-card quizz-test-card--interactive"
          key={test.id}
          onClick={() => handleOpen(test)}
          onKeyDown={(event) => {
            if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              handleOpen(test);
            }
          }}
          role="button"
          aria-label={`Open ${test.title}`}
          tabIndex={0}
        >
          <div className="quizz-test-top">
            <div className={`quizz-test-number ${colorClasses[index % colorClasses.length]}`}>{test.number}</div>

            <div className="quizz-test-info">
              <h3>{test.title}</h3>

              <div className="quizz-test-meta">
                <span className={`test-plan ${test.planClass}`}>{test.plan}</span>

                <span className="meta-item">
                  <i className="bi bi-calendar3"></i>
                  {test.date}
                </span>

                <span className="meta-item">
                  <i className="bi bi-clock"></i>
                  {test.duration}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="quiz-share-btn"
              aria-label={`Share ${test.title}`}
              onClick={(event) => event.stopPropagation()}
            >
              <img src={shareIcon} alt="" />
            </button>
          </div>

          <div className="quizz-progress-section">
            <div className="quizz-progress-line">
              <div
                className="quizz-progress-value"
                style={{
                  width: `${Math.min(Math.max(test.progress || 0, 0), 100)}%`,
                }}
              ></div>
            </div>
            <span className="quizz-progress-percent">{test.progress || 0}%</span>
          </div>

          <div className="test-status">
            <span className={test.status === "Locked" ? "locked" : "recent"}>{test.status}</span>

            {Number(test.coinUnlockCost) > 0 ? (
              <span className="coins-badge">
                <i className="fa-solid fa-coins"></i>
                {test.coinUnlockCost} Coins{test.canUnlockWithAdCoins ? ' · Coins Ready' : ''}
              </span>
            ) : null}
          </div>

          <div className="quizz-test-stats">
            <div className="test-stat">
              <div className="stat-icon">
                <i className="bi bi-chat-text"></i>
              </div>

              <div>
                <strong>{test.questions}</strong>
                <small>Questions</small>
              </div>
            </div>

            <div className="test-stat">
              <div className="stat-icon">
                <i className="bi bi-pencil-square"></i>
              </div>

              <div>
                <strong>{test.attempts}</strong>
                <small>Attempts</small>
              </div>
            </div>

            <div className="test-stat">
              <div className="stat-icon">
                <i className="bi bi-translate"></i>
              </div>

              <div>
                <strong>{test.language}</strong>
                <small></small>
              </div>
            </div>

            <div className="test-stat">
              <div className="stat-icon">
                <i className="bi bi-patch-check"></i>
              </div>

              <div>
                <strong>{test.marks}</strong>
                <small>Marks</small>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
    </>
  );
};

export default QuizzCard;

